import {
  getFileMimeFromArrayBuffer,
  toBlob,
  MIME,
  BufferEncoding,
  TimeArray,
  increaseTimeArray,
  formatTimeArray,
  FileReaderResponseTypeInBrowser,
  FetchWithAxios,
  BUFFER_ENCODING
} from "@zyzgroup/core-common";
import SparkMD5 from "spark-md5";

export function querySelectorAllToStringArray(
  query = 'script[type="text/js-worker"]'
): string[] {
  return Array.prototype.map.call(
    document.querySelectorAll(query),
    (oScript) => oScript.textContent
  ) as string[];
}

export const domToString = (el: HTMLElement) => {
  return new XMLSerializer().serializeToString(el);
};

export const stringToDom = (
  str: string,
  type: DOMParserSupportedType = "text/html"
) => {
  return new DOMParser().parseFromString(str, type);
};

export const domToBlob = (
  el: HTMLElement,
  options: {
    type?: string;
    mime?: MIME;
    encode?: BufferEncoding;
  } = {
    mime: MIME["image/svg+xml"],
    encode: BUFFER_ENCODING.utf8
  }
) => {
  return toBlob(domToString(el), options);
};

export async function selectFileInBrowser(
  accepts: string[] = ["*"],
  multiple?: boolean
): Promise<File[]> {
  // if (!document || !(document instanceof Document)) {
  //   throw new Error("This is not a browser environment");
  // }
  // 新API
  // window.showOpenFilePicker();
  // window.showDirectoryPicker();
  let files: File[] = [];
  const input = document.createElement("input") as HTMLInputElement;
  input.setAttribute("type", "file");
  input.setAttribute("visibility", "hidden");
  input.setAttribute("accept", accepts.join(","));
  if (multiple) {
    input.setAttribute("multiple", "true");
  }
  input.addEventListener(
    "click",
    (e) => {
      // e.preventDefault();
      // e.stopPropagation();
      // input.blur();
      input.value = ""; // 连续上传2次相同的文件时，清空 value 以触发 change 事件
    },
    false
  );
  return new Promise<File[]>((resolve, reject) => {
    input.addEventListener(
      "change",
      (e: Event) => {
        files = Array.from((e.target as HTMLInputElement).files || []);
        if (files.length === 0) {
          reject(new Error("Cancel"));
        }
        // else if (
        //   !accepts.includes("*") &&
        //   files.some((file) => !accepts.includes(`.${getFileExtension(file)}`))
        // ) {
        //   reject(new Error(`Please select files in ${accepts} format`));
        // }
        else {
          resolve(files);
        }
      },
      false
    );
    window.addEventListener(
      "focus",
      () => {
        setTimeout(() => {
          if (files.length === 0) {
            reject(new Error("Cancel"));
          }
        }, 1000);
      },
      { once: true }
    );
    // input.dispatchEvent(new MouseEvent("click", { bubbles: true }));
    input.click();
  })
    .then((files) => {
      input.remove();
      return files;
    })
    .catch((e) => {
      input.remove();
      throw e;
    });
}

export function readFileInBrowser<T extends string | ArrayBuffer>(
  // files: FileList,
  file: File | Blob,
  type: FileReaderResponseTypeInBrowser = FileReaderResponseTypeInBrowser.text,
  progressCb?: (loaded: number, total: number) => void
): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const reader = new FileReader();
    reader.addEventListener("progress", (e: ProgressEvent<FileReader>) => {
      // e: FileReaderEventMap["progress"]
      progressCb?.(e.loaded, e.total);
    });
    reader.addEventListener("load", (e: FileReaderEventMap["load"]) => {
      resolve(e.target?.result as T);
      // resolve(reader.result as T);
    });
    reader.addEventListener("error", (e: FileReaderEventMap["error"]) => {
      reject(e);
    });
    switch (type) {
      case FileReaderResponseTypeInBrowser.dataURL:
        reader.readAsDataURL(file);
        break;
      case FileReaderResponseTypeInBrowser.binaryString:
        reader.readAsBinaryString(file);
        break;
      case FileReaderResponseTypeInBrowser.arraybuffer:
        reader.readAsArrayBuffer(file);
        break;
      case FileReaderResponseTypeInBrowser.text:
      default:
        reader.readAsText(file);
    }
  });
}

export async function getFileMimeFromFileInBrowser(file: File): Promise<MIME> {
  const arrayBuffer = await readFileInBrowser<ArrayBuffer>(
    file,
    FileReaderResponseTypeInBrowser.arraybuffer
  );
  return getFileMimeFromArrayBuffer(arrayBuffer);
}

function readFileMD5Hash(
  resolve: (v: string) => void,
  reject: (e: any) => void,
  spark: SparkMD5.ArrayBuffer,
  file: File,
  chunkSize: number,
  start: number,
  end: number
) {
  const fileReader = new FileReader();
  fileReader.addEventListener("load", (e) => {
    if (!e.target || !e.target.result) {
      return reject(e);
    }
    spark.append(e.target.result as ArrayBuffer);
    if (end < file.size) {
      readFileMD5Hash(
        resolve,
        reject,
        spark,
        file,
        chunkSize,
        end,
        Math.min(end + chunkSize, file.size)
      );
    } else {
      return resolve(spark.end());
    }
  });
  fileReader.addEventListener("error", (e) => {
    return reject(e);
  });
  fileReader.readAsArrayBuffer(file.slice(start, end));
}

// 获取文件 MD5 hash
export async function getFileMD5Hash(file: File, chunkSize = 1 * 1024 * 1024) {
  return await new Promise<string>((resolve, reject) =>
    readFileMD5Hash(
      resolve,
      reject,
      new SparkMD5.ArrayBuffer(),
      file,
      chunkSize,
      0,
      Math.min(chunkSize, file.size)
    )
  );
}

export class FileUpload {
  axios: FetchWithAxios;
  // 获取服务器端 文件 MD5 hash 地址
  checkURL: string;
  // 文件上传地址
  uploadURL: string;
  // 要上传的文件
  file: File;
  // 本地文件 MD5 hash
  fileHash?: string;
  // 文件切割块大小
  chunkSize: number;
  // 文件总切割数量
  chunks: number;
  // 当前块数
  currentChunk = 0;
  // 是否正在上传
  isUploading = false;
  begin = 0;
  end = 0;
  timer?: ReturnType<typeof setInterval>;
  time: TimeArray = [0, 0, 0, 0, 0, 0];
  onExist?: () => void;
  onStart?: () => void;
  onProgress?: (currentChunk: number, chunks: number) => void;
  onComplete?: () => void;
  onTime?: (time: string) => void;

  constructor(options: {
    baseURL: string;
    checkURL: string;
    uploadURL: string;
    file: File;
    chunkSize?: number;
    onExist?: () => void;
    onStart?: () => void;
    onProgress?: (currentChunk: number, chunks: number) => void;
    onComplete?: () => void;
    onTime?: (time: string) => void;
  }) {
    this.axios = new FetchWithAxios({
      baseURL: options.baseURL,
      interceptors: {
        response: (response) => {
          if (response.config.url == this.uploadURL) {
            if (response.status == 200) {
              this.onProgress?.(this.currentChunk++, this.chunks);
              if (this.end == this.file.size) {
                this.isUploading = false;
                this.stopTimeCount();
                this.onComplete?.();
              } else {
                if (this.isUploading) {
                  this.uploadChunk();
                }
              }
            } else {
              // UNDO: 上传失败，尝试重传
            }
          }
        }
      }
    });
    this.checkURL = options.checkURL;
    this.uploadURL = options.uploadURL;
    this.file = options.file;
    this.chunkSize = options.chunkSize || 1 * 1024 * 1024;
    this.chunks = Math.ceil(this.file.size / this.chunkSize);
    this.onExist = options.onExist;
    this.onStart = options.onStart;
    this.onProgress = options.onProgress;
    this.onComplete = options.onComplete;
    this.onTime = options.onTime;
  }

  public async start() {
    if (!this.fileHash) {
      this.fileHash = await getFileMD5Hash(this.file, this.chunkSize);
    }
    // 后台判断文件是否已存在
    const result = await this.axios.post(this.checkURL, {
      fileName: this.file.name,
      fileHash: this.fileHash
    });
    if (result.data.isExist) {
      this.isUploading = false;
      this.onExist?.();
    } else {
      if (!this.isUploading) {
        this.onStart?.();
        this.startTimeCount();
        await this.uploadChunk();
      }
    }
  }

  public pause() {
    if (this.isUploading) {
      this.isUploading = false;
      this.stopTimeCount();
    }
  }

  private async uploadChunk() {
    this.isUploading = true;
    this.begin = this.currentChunk * this.chunkSize;
    this.end = Math.min(this.begin + this.chunkSize, this.file.size);
    const blob = this.file.slice(this.begin, this.end);
    const fd = new FormData();
    fd.append("fileData", blob);
    fd.append("fileName", this.file.name);
    fd.append("fileHash", this.fileHash!);
    fd.append("chunkIndex", String(this.currentChunk));
    fd.append("chunks", String(this.chunks));
    await this.axios.postFormData(this.uploadURL, fd);
  }

  private startTimeCount() {
    this.stopTimeCount();
    this.timer = setInterval(() => {
      increaseTimeArray(this.time, 1);
      this.onTime?.(formatTimeArray(this.time));
    }, 1000);
  }

  private stopTimeCount() {
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = undefined;
    }
  }
}
